import { useState } from "react";

import Modal from "@/components/modal";
import Toggle from "@/components/toggle";

function PauseTogglePanel({
  paused,
  isLoading,
  onSetPaused,
}: {
  paused: boolean;
  isLoading: boolean;
  onSetPaused: (paused: boolean) => Promise<unknown>;
}) {
  const [pendingValue, setPendingValue] = useState<boolean | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const handleConfirm = async () => {
    if (pendingValue === null) {
      return;
    }
    setIsSaving(true);
    try {
      await onSetPaused(pendingValue);
    } finally {
      setIsSaving(false);
      setPendingValue(null);
    }
  };

  return (
    <section
      className={`rounded-lg border px-5 py-4 ${
        paused
          ? "border-rose-100 bg-rose-50"
          : "border-slate-200 bg-white"
      }`}
    >
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-blue-200">
            Tiering Workers
          </h2>
          <p className={`text-sm ${paused ? "text-rose-700" : "text-grey-200"}`}>
            {isLoading
              ? "Loading pause state..."
              : paused
                ? "PAUSED — markers and appliers stop after their current batch."
                : "Running — pausing stops all tiering work after in-flight batches."}
          </p>
        </div>
        <Toggle
          value={!paused}
          onChange={(running) => setPendingValue(!running)}
          label={paused ? "Paused" : "Running"}
          labelClassName={paused ? "text-rose-700 font-semibold" : "text-blue-300"}
        />
      </div>

      <Modal
        isOpen={pendingValue !== null}
        onClose={() => setPendingValue(null)}
        panelClassName="w-full max-w-md rounded-xl bg-white p-6 shadow-xl"
      >
        <h3 className="text-lg font-semibold text-blue-200">
          {pendingValue ? "Pause all tiering work?" : "Resume tiering work?"}
        </h3>
        <p className="mt-2 text-sm text-grey-200">
          {pendingValue
            ? "Workers finish their current batch and stop. In-flight rows are swept back safely. No state is lost."
            : "Workers resume exactly where they left off."}
        </p>
        <div className="mt-5 flex justify-end gap-3">
          <button
            type="button"
            className="ox-button !mt-0 w-auto !rounded-[8px] !px-4 !py-1.5 !text-sm"
            onClick={() => setPendingValue(null)}
            disabled={isSaving}
          >
            Keep as is
          </button>
          <button
            type="button"
            className="ox-red-button !mt-0 w-auto !rounded-[8px] !px-4 !py-1.5 !text-sm"
            onClick={handleConfirm}
            disabled={isSaving}
          >
            {isSaving
              ? "Saving..."
              : pendingValue
                ? "Pause tiering"
                : "Resume tiering"}
          </button>
        </div>
      </Modal>
    </section>
  );
}

export default PauseTogglePanel;
